import type { Config, CoreProjection } from '../../_contract/config';
import type { CoreLayout } from './coreLayout';
import { collectPackageTree } from './packageTree';
import type { Vector3Tuple } from '@toolproof-core/visualization/_contract/geometry';

export function deriveLayeredTree(projection: CoreProjection, config: Config) {
    const settings = config.layeredTree ?? { levelGap: 24, rowGap: 8, laneGap: 48 };
    const directoryRadius = config.geometry.directoryRadius * config.geometry.renderRadiusMultiplier;
    const fileRadius = config.geometry.fileRadius * config.geometry.renderRadiusMultiplier;
    const rootRadius = config.geometry.rootRadius * config.geometry.renderRadiusMultiplier;
    const gap = config.geometry.minimumSubtreeGap;
    if (![settings.levelGap, settings.rowGap, settings.laneGap, directoryRadius, fileRadius, rootRadius, gap]
        .every(value => Number.isFinite(value) && value > 0)) {
        throw new Error('Layered-tree spacing and node radii must be finite positive numbers.');
    }
    if (fileRadius * 2 / Math.sqrt(3) < 0.0001) throw new Error('Layered-tree file radius is below the cube renderer minimum.');
    const nodes = collectPackageTree(projection);
    const byPath = new Map(nodes.map(node => [node.path, node]));
    const boundaryOrder = ['_contract', '_data', '_pure'];
    const boundaryRank = (path: string) => {
        const node = byPath.get(path)!;
        const index = node.nodeKind === 'directory' ? boundaryOrder.indexOf(node.name) : -1;
        return index < 0 ? boundaryOrder.length : index;
    };
    for (const node of nodes) {
        if (node.depth === 0) continue;
        node.childPaths.sort((a, b) => boundaryRank(a) - boundaryRank(b));
    }
    const maxRadius = Math.max(directoryRadius, fileRadius, rootRadius);
    const rowGap = Math.max(settings.rowGap, maxRadius * 2 + gap);
    const levelGap = Math.max(settings.levelGap, maxRadius * 2 + gap);
    const laneGap = Math.max(settings.laneGap, maxRadius * 2 + gap,
        ...nodes.map(node => node.name.length * Math.max(0.3, (node.nodeKind === 'file' ? fileRadius : directoryRadius) * 0.7) * 0.62 + gap * 2));
    const rowsByPath = new Map<string, number>();
    for (const node of [...nodes].reverse()) {
        rowsByPath.set(node.path, node.childPaths.length
            ? node.childPaths.reduce((sum, path) => sum + rowsByPath.get(path)!, 0) : 1);
    }
    const packageOrder = ['genesis', 'validation', 'lib', 'visualization', 'cosmos', 'engine',
        'persistence-server', 'language-server', 'vscode-extension', 'forge'];
    const packageRank = (name: string) => {
        const index = packageOrder.indexOf(name);
        return index < 0 ? packageOrder.length : index;
    };
    const packages = nodes.filter(node => node.depth === 1).sort((a, b) =>
        packageRank(a.name) - packageRank(b.name)
            || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
    const layout: CoreLayout = { entryByPath: new Map(), ringCenterByPath: new Map(),
        ringRadiusByPath: new Map(), boundsRadius: rootRadius };
    const radiusByPath = new Map<string, number>();
    layout.entryByPath.set('.', { position: [0, 0, 0], parentPath: null, angle: 0,
        visualRadius: rootRadius, subtreeRadius: rootRadius });
    radiusByPath.set('.', rootRadius);
    const pending = packages.map((node, index) => ({ node,
        x: (index - (packages.length - 1) / 2) * laneGap, top: rowsByPath.get(node.path)! * rowGap / 2 }));
    for (let cursor = 0; cursor < pending.length; cursor++) {
        const { node, x, top } = pending[cursor]!;
        const height = rowsByPath.get(node.path)! * rowGap;
        const radius = node.nodeKind === 'directory' ? directoryRadius : fileRadius;
        const position: [number, number, number] = [x, top - height / 2, -node.depth * levelGap];
        if (!position.every(Number.isFinite) || Math.hypot(...position) * Number.EPSILON > radius * 1e-4) {
            throw new Error(`Layered-tree layout exceeds numeric precision at ${node.path}.`);
        }
        layout.entryByPath.set(node.path, { position, parentPath: node.parentPath, angle: 0,
            visualRadius: radius, subtreeRadius: radius });
        radiusByPath.set(node.path, radius);
        layout.boundsRadius = Math.max(layout.boundsRadius, Math.hypot(...position) + radius);
        let childTop = top;
        for (const path of node.childPaths) {
            pending.push({ node: byPath.get(path)!, x, top: childTop });
            childTop -= rowsByPath.get(path)! * rowGap;
        }
    }
    const initialBoundsRadius = Math.max(rootRadius,
        ...packages.map(node => Math.hypot(...layout.entryByPath.get(node.path)!.position) + directoryRadius));
    const connections: { key: string; fromPath: string; toPath: string; from: Vector3Tuple; to: Vector3Tuple }[] = [];
    for (const node of nodes) {
        if (!node.childPaths.length) continue;
        const from = layout.entryByPath.get(node.path)!.position;
        const children = node.childPaths.map(path => ({ path, position: layout.entryByPath.get(path)!.position }));
        const busZ = (from[2] + children[0]!.position[2]) / 2;
        const joint: Vector3Tuple = [from[0], from[1], busZ];
        connections.push({ key: `trunk:${node.path}`, fromPath: node.path, toPath: node.path, from, to: joint });
        const axis = node.depth === 0 ? 0 : 1;
        const minimum = Math.min(from[axis], ...children.map(child => child.position[axis]));
        const maximum = Math.max(from[axis], ...children.map(child => child.position[axis]));
        if (minimum < maximum) {
            const busFrom: Vector3Tuple = [...joint], busTo: Vector3Tuple = [...joint];
            busFrom[axis] = minimum;
            busTo[axis] = maximum;
            connections.push({ key: `bus:${node.path}`, fromPath: node.path, toPath: node.path, from: busFrom, to: busTo });
        }
        for (const child of children) {
            connections.push({ key: `child:${child.path}`, fromPath: node.path, toPath: child.path,
                from: [child.position[0], child.position[1], busZ], to: child.position });
        }
    }
    return { nodes, layout, radiusByPath, initialBoundsRadius, connections };
}
